import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { CreditCard, Users, TrendingDown, Calculator, ArrowRight, CheckCircle2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalculatorSection = ({ onStartForm }) => {
  const [debtAmount, setDebtAmount] = useState(20000);
  const [monthlyIncome, setMonthlyIncome] = useState(2000);
  const [creditorCount, setCreditorCount] = useState(4);
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchCalculation();
    }, 400);
    return () => clearTimeout(debounce);
  }, [debtAmount, monthlyIncome, creditorCount]);

  const fetchCalculation = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/calculator`, {
        debt_amount: debtAmount,
        monthly_income: monthlyIncome,
        number_of_creditors: creditorCount
      });
      setResult(response.data);
    } catch (error) {
      console.error("Calculator error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Calculate donut chart segments
  const calculateDonutData = () => {
    if (!result) return { paid: 0, written: 0 };
    const totalPaid = result.estimated_iva_payment * result.repayment_term_months;
    const writtenOff = Math.max(0, debtAmount - totalPaid);
    const paidPercent = (totalPaid / debtAmount) * 100;
    const writtenPercent = 100 - paidPercent;
    return { paid: paidPercent, written: writtenPercent, totalPaid, writtenOff };
  };

  const donutData = calculateDonutData();
  const circumference = 2 * Math.PI * 45;
  const paidStroke = (donutData.paid / 100) * circumference;
  const writtenStroke = (donutData.written / 100) * circumference;

  return (
    <section className="section-padding bg-[#F8FAFC]" data-testid="calculator-section">
      <div className="container-main">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-[#059669] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Calculator className="w-4 h-4" />
            Interactive Calculator
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
            See How Much You Could Save
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Adjust the sliders below to get a personalised estimate of your potential IVA savings
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left - Controls */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-slate-100"
          >
            {/* Debt Amount Slider */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0F172A]">Total Debt</p>
                  <p className="text-sm text-slate-500">How much do you owe?</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="text-center mb-4">
                  <span className="font-numbers text-3xl font-bold text-[#0F172A]" data-testid="calc-debt-display">
                    {formatCurrency(debtAmount)}
                  </span>
                </div>
                <Slider
                  value={[debtAmount]}
                  onValueChange={(value) => setDebtAmount(value[0])}
                  min={5000}
                  max={75000}
                  step={500}
                  className="w-full"
                  data-testid="calc-debt-slider"
                />
                <div className="flex justify-between mt-2 text-xs text-slate-400">
                  <span>£5,000</span>
                  <span>£75,000+</span>
                </div>
              </div>
            </div>

            {/* Monthly Income Slider */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-[#0F172A]">Monthly Income</p>
                  <p className="text-sm text-slate-500">Your take-home pay</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="text-center mb-4">
                  <span className="font-numbers text-3xl font-bold text-[#0F172A]" data-testid="calc-income-display">
                    {formatCurrency(monthlyIncome)}
                  </span>
                </div>
                <Slider
                  value={[monthlyIncome]}
                  onValueChange={(value) => setMonthlyIncome(value[0])}
                  min={500}
                  max={5000}
                  step={100}
                  className="w-full"
                  data-testid="calc-income-slider"
                />
                <div className="flex justify-between mt-2 text-xs text-slate-400">
                  <span>£500</span>
                  <span>£5,000+</span>
                </div>
              </div>
            </div>

            {/* Creditors Counter */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-[#0F172A]">Number of Creditors</p>
                  <p className="text-sm text-slate-500">How many do you owe to?</p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-4 bg-slate-50 rounded-xl p-4">
                <button
                  onClick={() => setCreditorCount(Math.max(1, creditorCount - 1))}
                  className="w-12 h-12 rounded-full bg-white border-2 border-slate-200 text-slate-600 font-bold text-xl hover:border-[#10B981] hover:text-[#10B981] transition-colors"
                  data-testid="creditor-minus"
                >
                  -
                </button>
                <span className="font-numbers text-4xl font-bold text-[#0F172A] w-16 text-center" data-testid="creditor-count">
                  {creditorCount}
                </span>
                <button
                  onClick={() => setCreditorCount(Math.min(20, creditorCount + 1))}
                  className="w-12 h-12 rounded-full bg-white border-2 border-slate-200 text-slate-600 font-bold text-xl hover:border-[#10B981] hover:text-[#10B981] transition-colors"
                  data-testid="creditor-plus"
                >
                  +
                </button>
              </div>
            </div>
          </motion.div>

          {/* Right - Results */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-slate-100"
          >
            {result && (
              <div className="space-y-6">
                {/* Eligibility Status */}
                <div className={`p-4 rounded-xl ${result.iva_eligible ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className={`w-6 h-6 ${result.iva_eligible ? 'text-[#10B981]' : 'text-amber-500'}`} />
                    <div>
                      <p className={`font-semibold ${result.iva_eligible ? 'text-[#059669]' : 'text-amber-700'}`}>
                        {result.iva_eligible ? 'You May Qualify for an IVA' : 'More Information Needed'}
                      </p>
                      <p className="text-sm text-slate-600">{result.eligibility_reason}</p>
                    </div>
                  </div>
                </div>

                {/* Donut Chart */}
                <div className="flex justify-center py-4">
                  <div className="relative">
                    <svg width="180" height="180" viewBox="0 0 100 100" className="donut-chart">
                      {/* Background circle */}
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="#E2E8F0"
                        strokeWidth="10"
                      />
                      {/* Written off segment */}
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="#10B981"
                        strokeWidth="10"
                        strokeDasharray={`${writtenStroke} ${circumference}`}
                        strokeDashoffset="0"
                        transform="rotate(-90 50 50)"
                        className="transition-all duration-500"
                      />
                      {/* Paid segment */}
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="#0F172A"
                        strokeWidth="10"
                        strokeDasharray={`${paidStroke} ${circumference}`}
                        strokeDashoffset={-writtenStroke}
                        transform="rotate(-90 50 50)"
                        className="transition-all duration-500"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <p className="text-xs text-slate-500 uppercase">Written Off</p>
                      <p className="font-numbers text-2xl font-bold text-[#10B981]">
                        {Math.round(donutData.written)}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Legend */}
                <div className="flex justify-center gap-8 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#10B981]"></div>
                    <span className="text-slate-600">Debt Written Off</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#0F172A]"></div>
                    <span className="text-slate-600">Amount Repaid</span>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 uppercase mb-1">Your New Monthly Payment</p>
                    <p className="font-numbers text-2xl font-bold text-[#0F172A]" data-testid="result-monthly-payment">
                      {formatCurrency(result.estimated_iva_payment)}
                    </p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 uppercase mb-1">Monthly Savings</p>
                    <p className="font-numbers text-2xl font-bold text-[#10B981]" data-testid="result-monthly-savings">
                      {formatCurrency(result.estimated_monthly_savings)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 uppercase mb-1">Debt-to-Income Ratio</p>
                    <p className="font-numbers text-2xl font-bold text-[#0F172A]" data-testid="result-dti">
                      {result.debt_to_income_ratio}%
                    </p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 uppercase mb-1">Total Potential Savings</p>
                    <p className="font-numbers text-2xl font-bold text-[#10B981]" data-testid="result-total-savings">
                      {formatCurrency(result.estimated_total_savings)}*
                    </p>
                  </div>
                </div>

                {/* CTA */}
                <Button
                  onClick={() => onStartForm(debtAmount)}
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-semibold py-6 text-lg rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-emerald-200"
                  data-testid="calc-cta-btn"
                >
                  Get Your Free Assessment
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>

                <p className="text-xs text-slate-400 text-center">
                  *Subject to eligibility. Figures are estimates only.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CalculatorSection;
